/**
 * File utility functions for file name sanitization and manipulation
 */

/**
 * Sanitizes a filename by removing problematic characters
 * Keeps the file extension and replaces special characters with safe alternatives
 *
 * @param fileName - The original filename to sanitize
 * @returns The sanitized filename
 *
 * @example
 * sanitizeFileName("15,00 € - B31B2F53-0176.pdf")
 * // Returns: "15_00_EUR_B31B2F53_0176.pdf"
 *
 * @example
 * sanitizeFileName("Invoice $100.50 - March.pdf")
 * // Returns: "Invoice_USD100_50_March.pdf"
 */
export function sanitizeFileName(fileName: string): string {
    // Split filename and extension
    const lastDotIndex = fileName.lastIndexOf('.');
    const name = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
    const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';

    // Sanitize the name part
    let sanitized = name
        // Replace currency symbols with text equivalents
        .replace(/€/g, 'EUR')
        .replace(/\$/g, 'USD')
        .replace(/£/g, 'GBP')
        // Replace commas and semicolons with underscores
        .replace(/[,;]/g, '_')
        // Replace spaces and multiple separators with single underscore
        .replace(/[\s\-]+/g, '_')
        // Remove or replace other special characters (keep only alphanumeric and underscore)
        .replace(/[^\w._-]/g, '')
        // Remove multiple consecutive underscores
        .replace(/_+/g, '_')
        // Remove leading/trailing underscores
        .replace(/^_+|_+$/g, '');

    // Sanitize extension (keep only alphanumeric and the dot)
    const sanitizedExtension = extension.replace(/[^\w.]/g, '');

    // If name is empty after sanitization, use a default name with timestamp
    if (!sanitized) {
        sanitized = `file_${Date.now()}`;
    }

    return sanitized + sanitizedExtension;
}

/**
 * Options for image compression
 */
export interface ImageCompressionOptions {
    /** Quality of the compressed image (0-1, default: 0.8) */
    quality?: number;
    /** Maximum width in pixels (default: 1920) */
    maxWidth?: number;
    /** Maximum height in pixels (default: 1920) */
    maxHeight?: number;
    /** Maximum file size in bytes (default: 2MB) */
    maxSizeBytes?: number;
    /** MIME type for the output (default: 'image/jpeg') */
    outputType?: string;
}

/**
 * Compresses an image file to reduce its size while maintaining acceptable quality
 * Uses Canvas API to resize and compress the image
 *
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns A Promise that resolves to a compressed File object
 *
 * @example
 * const compressedFile = await compressImage(originalFile, {
 *     quality: 0.8,
 *     maxWidth: 1920,
 *     maxHeight: 1920,
 *     maxSizeBytes: 2 * 1024 * 1024 // 2MB
 * });
 */
export async function compressImage(
    file: File,
    options: ImageCompressionOptions = {}
): Promise<File> {
    // Check if DOM APIs are available (skip compression in non-browser environments)
    if (typeof document === 'undefined' || typeof Image === 'undefined' || typeof FileReader === 'undefined') {
        console.warn('[PERF] compressImage - DOM APIs not available, returning original file');
        return file;
    }

    const startTime = performance.now();
    const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    console.log(`[PERF] compressImage START - File: ${file.name}, Size: ${originalSizeMB}MB`);

    const {
        quality = 0.8,
        maxWidth = 1920,
        maxHeight = 1920,
        maxSizeBytes = 2 * 1024 * 1024, // 2MB default
        outputType = 'image/jpeg'
    } = options;

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
        console.warn(`[PERF] compressImage - Not an image file, returning original: ${file.type}`);
        return file;
    }

    // If file is already small enough, return as-is
    if (file.size <= maxSizeBytes) {
        const compressionTime = performance.now() - startTime;
        console.log(`[PERF] compressImage END - File already small enough, skipped compression: ${compressionTime.toFixed(2)}ms`);
        return file;
    }

    try {
        // Load image
        const image = await loadImageFromFile(file);
        
        // Calculate new dimensions maintaining aspect ratio
        const { width, height } = calculateDimensions(
            image.width,
            image.height,
            maxWidth,
            maxHeight
        );

        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
            throw new Error('Could not get canvas context');
        }

        ctx.drawImage(image, 0, 0, width, height);

        // Convert to blob with compression
        let blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Failed to compress image'));
                    }
                },
                outputType,
                quality
            );
        });

        // If still too large, reduce quality progressively
        let currentQuality = quality;
        let attempts = 0;
        const maxAttempts = 5;
        const qualityStep = 0.1;

        while (blob.size > maxSizeBytes && attempts < maxAttempts && currentQuality > 0.3) {
            currentQuality -= qualityStep;
            attempts++;
            
            blob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Failed to compress image'));
                        }
                    },
                    outputType,
                    currentQuality
                );
            });

            console.log(`[PERF] compressImage - Attempt ${attempts}, Quality: ${currentQuality.toFixed(2)}, Size: ${(blob.size / (1024 * 1024)).toFixed(2)}MB`);
        }

        // Create new File from compressed blob
        const compressedFileName = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
        const compressedFile = new File([blob], compressedFileName, {
            type: outputType,
            lastModified: Date.now()
        });

        const compressionTime = performance.now() - startTime;
        const compressedSizeMB = (compressedFile.size / (1024 * 1024)).toFixed(2);
        const reductionPercent = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
        
        console.log(`[PERF] compressImage END - Compression time: ${compressionTime.toFixed(2)}ms, Original: ${originalSizeMB}MB, Compressed: ${compressedSizeMB}MB, Reduction: ${reductionPercent}%`);

        return compressedFile;
    } catch (error) {
        const compressionTime = performance.now() - startTime;
        console.error(`[PERF] compressImage ERROR - Failed after ${compressionTime.toFixed(2)}ms:`, error);
        // Return original file if compression fails
        console.warn('[PERF] compressImage - Returning original file due to compression error');
        return file;
    }
}

/**
 * Loads an image from a File object
 */
function loadImageFromFile(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        // Double-check DOM APIs are available
        if (typeof FileReader === 'undefined' || typeof Image === 'undefined') {
            reject(new Error('DOM APIs not available'));
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = e.target?.result as string;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

/**
 * Calculates new dimensions maintaining aspect ratio
 */
function calculateDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
): { width: number; height: number } {
    let width = originalWidth;
    let height = originalHeight;

    // Calculate scaling factor to fit within max dimensions
    if (width > maxWidth || height > maxHeight) {
        const scaleWidth = maxWidth / width;
        const scaleHeight = maxHeight / height;
        const scale = Math.min(scaleWidth, scaleHeight);
        
        width = Math.round(width * scale);
        height = Math.round(height * scale);
    }

    return { width, height };
}
